import { links } from '../content/site';
import type { StaticPage } from '../routes';
import { articles } from '../content/articles';
import { getBlogBreadcrumbs } from '../content/blog-breadcrumbs';

const PRODUCTION_ORIGIN = 'https://onchainden.com';
export const STRUCTURED_DATA_ID = 'page-structured-data';

export function getRobotsContent(page: StaticPage, origin: string): string {
  return page.noIndex || origin !== PRODUCTION_ORIGIN
    ? 'noindex,nofollow'
    : 'index,follow,max-image-preview:large';
}

/** Shared by the HTML build and client navigation so crawlers see one graph. */
export function getStructuredData(page: StaticPage, origin: string) {
  if (page.noIndex) return null;
  const url = origin + page.canonicalPath;
  const organizationId = `${origin}/#organization`;
  const websiteId = `${origin}/#website`;
  const isBlogCollection = page.path === '/blog' || Boolean(page.blogCategory);
  const breadcrumbs =
    page.article || page.blogCategory
      ? getBlogBreadcrumbs(
          page.article?.category ?? page.blogCategory,
          page.article
            ? { title: page.article.headline, href: page.canonicalPath }
            : undefined
        )
      : [];
  const breadcrumbId = `${url}#breadcrumbs`;
  const hasBreadcrumbs = breadcrumbs.length > 1;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': organizationId,
        name: 'Den',
        url: `${origin}/`,
        logo: `${origin}/den-logo.svg`,
        sameAs: [links.github, links.x, links.linkedin],
      },
      {
        '@type': 'WebSite',
        '@id': websiteId,
        name: 'Den',
        url: `${origin}/`,
        publisher: { '@id': organizationId },
        inLanguage: 'en',
      },
      {
        '@type': isBlogCollection ? 'CollectionPage' : 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: page.title,
        description: page.description,
        isPartOf: { '@id': websiteId },
        about: { '@id': organizationId },
        inLanguage: 'en',
        ...(hasBreadcrumbs && { breadcrumb: { '@id': breadcrumbId } }),
        ...(isBlogCollection && {
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: articles
              .filter(
                (article) =>
                  !page.blogCategory || article.category === page.blogCategory
              )
              .map((article, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: article.title,
                url: `${origin}/blog/${article.slug}`,
              })),
          },
        }),
      },
      ...(hasBreadcrumbs
        ? [
            {
              '@type': 'BreadcrumbList',
              '@id': breadcrumbId,
              itemListElement: breadcrumbs.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.label,
                item: origin + item.href,
              })),
            },
          ]
        : []),
    ],
  };
}

export function serializeStructuredData(value: unknown): string {
  // A literal closing script tag in copy must never terminate the JSON-LD block.
  return JSON.stringify(value).replace(/</g, '\\u003c');
}
